import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Badge } from '@atoms/Badge';
import { Spinner } from '@atoms/Spinner';
import { campaignService } from '@services/campaignService';
import { catalogService } from '@services/catalogService';
import { formatCurrency } from '@utils/formatters';
import { Modal } from '@atoms/Modal';

const TYPE_LABELS: Record<string, string> = {
  quantity_discount: 'Miktar İndirimi',
  buy_x_pay_y: 'X Al Y Öde',
  flash_sale: 'Flaş İndirim',
  bundle: 'Paket',
};

import type { Campaign, Product } from '@/types';

export function CampaignsSection() {
  const queryClient = useQueryClient();
  const [detailCampaign, setDetailCampaign] = useState<Campaign | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'quantity_discount',
    productIds: [] as string[],
    minQuantity: '',
    discountRate: '',
    buyQuantity: '',
    payQuantity: '',
    bundlePrice: '',
    startDate: '',
    endDate: '',
  });

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['wholesaler', 'campaigns'],
    queryFn: () => campaignService.getCampaigns(),
  });

  const { data: products } = useQuery({
    queryKey: ['wholesaler', 'products'],
    queryFn: () => catalogService.getMyProducts(),
  });

  useEffect(() => {
    if (form.type === 'bundle' && products?.length && form.productIds.length === 0) {
      setForm((prev) => ({
        ...prev,
        productIds: products.slice(0, 2).map((p: Product) => p.id),
      }));
    }
  }, [form.type, products, form.productIds.length]);

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      type: 'quantity_discount',
      productIds: [],
      minQuantity: '',
      discountRate: '',
      buyQuantity: '',
      payQuantity: '',
      bundlePrice: '',
      startDate: '',
      endDate: '',
    });
    setEditingId(null);
  };

  const openEdit = (c: Campaign) => {
    setEditingId(c.id);
    setShowForm(true);
    setForm({
      title: c.title,
      description: c.description ?? '',
      type: c.type,
      productIds: c.productIds ?? [],
      minQuantity: c.minQuantity != null ? String(c.minQuantity) : '',
      discountRate: c.discountRate != null ? String(c.discountRate) : '',
      buyQuantity: c.buyQuantity != null ? String(c.buyQuantity) : '',
      payQuantity: c.payQuantity != null ? String(c.payQuantity) : '',
      bundlePrice: c.bundlePrice != null ? String(c.bundlePrice) : '',
      startDate: c.startDate ?? '',
      endDate: c.endDate ?? '',
    });
  };

  const buildPayload = () => ({
    title: form.title,
    description: form.description || undefined,
    type: form.type,
    productIds: form.productIds.length ? form.productIds : undefined,
    minQuantity: form.minQuantity ? Number(form.minQuantity) : undefined,
    discountRate: form.discountRate ? Number(form.discountRate) : undefined,
    buyQuantity: form.buyQuantity ? Number(form.buyQuantity) : undefined,
    payQuantity: form.payQuantity ? Number(form.payQuantity) : undefined,
    bundlePrice: form.bundlePrice ? Number(form.bundlePrice) : undefined,
    startDate: form.startDate || undefined,
    endDate: form.endDate || undefined,
  });

  const createMutation = useMutation({
    mutationFn: () => campaignService.createCampaign(buildPayload()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'campaigns'] });
      setShowForm(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => campaignService.updateCampaign(editingId!, buildPayload()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'campaigns'] });
      setShowForm(false);
      resetForm();
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => campaignService.toggleCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wholesaler', 'campaigns'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => campaignService.deleteCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['wholesaler', 'campaigns'] }),
  });

  const toggleProduct = (productId: string) => {
    setForm((prev) => ({
      ...prev,
      productIds: prev.productIds.includes(productId)
        ? prev.productIds.filter((id) => id !== productId)
        : [...prev.productIds, productId],
    }));
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kampanyalar</h1>
          <p className="mt-1 text-sm text-slate-500">İndirim ve promosyon yönetimi</p>
        </div>
        <Button size="sm" onClick={() => { if (showForm) { setShowForm(false); resetForm(); } else setShowForm(true); }}>
          {showForm ? 'İptal' : 'Yeni Kampanya'}
        </Button>
      </div>

      {showForm && (
        <Card className="card-elevated space-y-3">
          <h3 className="font-medium">{editingId ? 'Kampanya Düzenle' : 'Yeni Kampanya'}</h3>
          <Input label="Başlık" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input label="Açıklama" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value, productIds: [] })}
          >
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {form.type === 'quantity_discount' && (
            <>
              <Input label="Min. Miktar" type="number" value={form.minQuantity} onChange={(e) => setForm({ ...form, minQuantity: e.target.value })} />
              <Input label="İndirim (%)" type="number" value={form.discountRate} onChange={(e) => setForm({ ...form, discountRate: e.target.value })} />
            </>
          )}

          {form.type === 'flash_sale' && (
            <>
              <Input label="İndirim (%)" type="number" value={form.discountRate} onChange={(e) => setForm({ ...form, discountRate: e.target.value })} />
              <Input label="Başlangıç" type="datetime-local" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              <Input label="Bitiş" type="datetime-local" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
            </>
          )}

          {form.type === 'buy_x_pay_y' && (
            <>
              <Input label="Al (X)" type="number" value={form.buyQuantity} onChange={(e) => setForm({ ...form, buyQuantity: e.target.value })} />
              <Input label="Öde (Y)" type="number" value={form.payQuantity} onChange={(e) => setForm({ ...form, payQuantity: e.target.value })} />
            </>
          )}

          {form.type === 'bundle' && (
            <Input label="Paket Fiyatı" type="number" value={form.bundlePrice} onChange={(e) => setForm({ ...form, bundlePrice: e.target.value })} />
          )}

          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Ürünler</p>
            <div className="flex flex-wrap gap-2">
              {(products ?? []).map((p: Product) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleProduct(p.id)}
                  className={`rounded-full px-3 py-1 text-xs ${
                    form.productIds.includes(p.id) ? 'bg-brand-500 text-white' : 'bg-gray-100'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <Button
            loading={createMutation.isPending || updateMutation.isPending}
            disabled={!form.title}
            onClick={() => (editingId ? updateMutation.mutate() : createMutation.mutate())}
          >
            {editingId ? 'Güncelle' : 'Oluştur'}
          </Button>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {(campaigns ?? []).map((c: Campaign) => (
          <Card key={c.id}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-medium">{c.title}</h3>
                <p className="text-xs text-gray-500">{TYPE_LABELS[c.type] ?? c.type}</p>
              </div>
              <Badge variant={c.isActive ? 'success' : 'default'}>{c.isActive ? 'Aktif' : 'Pasif'}</Badge>
            </div>
            {c.description && <p className="mt-1 text-sm text-gray-600">{c.description}</p>}
            <p className="mt-1 text-xs text-gray-400">
              {c.discountRate != null && `%${c.discountRate} indirim`}
              {c.bundlePrice != null && `Paket: ${formatCurrency(c.bundlePrice)}`}
              {c.buyQuantity != null && c.payQuantity != null && `${c.buyQuantity} al ${c.payQuantity} öde`}
            </p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setDetailCampaign(c)}>Detay</Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(c)}>Düzenle</Button>
              <Button size="sm" variant="outline" onClick={() => toggleMutation.mutate(c.id)}>
                {c.isActive ? 'Durdur' : 'Başlat'}
              </Button>
              <Button size="sm" variant="danger" onClick={() => deleteMutation.mutate(c.id)}>Sil</Button>
            </div>
          </Card>
        ))}
      </div>

      <Modal open={Boolean(detailCampaign)} onClose={() => setDetailCampaign(null)} title={detailCampaign?.title ?? 'Kampanya Detayı'}>
        {detailCampaign && (
          <div className="space-y-2 text-sm">
            <p><strong>Tür:</strong> {TYPE_LABELS[detailCampaign.type] ?? detailCampaign.type}</p>
            {detailCampaign.description && <p>{detailCampaign.description}</p>}
            {detailCampaign.discountRate != null && <p>İndirim: %{detailCampaign.discountRate}</p>}
            {detailCampaign.minQuantity != null && <p>Min. miktar: {detailCampaign.minQuantity}</p>}
            {detailCampaign.buyQuantity != null && detailCampaign.payQuantity != null && (
              <p>{detailCampaign.buyQuantity} al {detailCampaign.payQuantity} öde</p>
            )}
            {detailCampaign.bundlePrice != null && <p>Paket fiyatı: {formatCurrency(detailCampaign.bundlePrice)}</p>}
            {detailCampaign.startDate && <p>Başlangıç: {detailCampaign.startDate}</p>}
            {detailCampaign.endDate && <p>Bitiş: {detailCampaign.endDate}</p>}
            <p>Durum: {detailCampaign.isActive ? 'Aktif' : 'Pasif'}</p>
            {detailCampaign.productIds?.length ? (
              <p>Ürün sayısı: {detailCampaign.productIds.length}</p>
            ) : null}
          </div>
        )}
      </Modal>
    </div>
  );
}
